<template>
  <div class="tx-list-page">
    <v-container fluid grid-list-xl>
      <v-layout row wrap>
        <v-flex xs12>
          <div class="list-hero">
            <p class="list-crumb">Transit / Trámites para Procesar</p>
            <h1 class="list-title">Trámites para Procesar</h1>
            <!-- Es una bandeja de pendientes, no el espejo del Listado de
                 Facturas: el cliente esperaba ver aquí todo lo creado allá.
                 El subtítulo lo dice y nombra el paso siguiente. -->
            <p class="list-subtitle">
              Marca las valijas listas para continuar con el trámite de las
              facturas. Solo aparecen los trámites cuya valija aún no se ha
              marcado; al iniciarlos pasan a
              <strong>Trámites en Proceso</strong>. Para ver un trámite en
              cualquier otro paso, usa el filtro
              <strong>Estado del trámite</strong> del Listado de Facturas.
            </p>
          </div>
        </v-flex>

        <v-flex xs12>
          <tx-filter @search="onSearch" @download="onDownload"></tx-filter>
        </v-flex>

        <v-flex xs12>
          <v-card class="tx-card">
            <div class="tx-toolbar">
              <div class="tx-toolbar__info">
                <span class="tx-chip">
                  <v-icon small class="tx-chip__icon">inventory_2</v-icon>
                  {{ meta.totalGroups }} por procesar
                  <template v-if="meta.totalInvoices !== meta.totalGroups">
                    ({{ meta.totalInvoices }} facturas)
                  </template>
                </span>
              </div>
            </div>
            <v-divider></v-divider>

            <v-card-text class="pa-0">
              <div class="table-wrap">
                <table class="tx-table">
                  <thead>
                    <tr>
                      <th># Factura</th>
                      <th>Distribuidor</th>
                      <th>Laboratorio</th>
                      <th>Fecha recepción</th>
                      <th>Inicio del trámite</th>
                      <th>Órgano Anuente</th>
                      <th class="th-center">Notaría</th>
                      <th>Comentarios</th>
                      <th>Estado</th>
                      <th class="th-center">Detalles</th>
                      <th class="th-center">Iniciar trámite</th>
                    </tr>
                  </thead>
                  <!-- Un <tbody> por trámite, como en Contabilidad: la banda
                       con el consecutivo y, debajo, una fila por factura. -->
                  <tbody
                    v-for="group in groups"
                    :key="group.key"
                    class="tx-group"
                  >
                    <!-- Banda del trámite (texto del mixin TramiteBand):
                         solo texto. Iniciar va una sola vez dentro del
                         trámite, en su columna; en un consolidado inicia el
                         trámite entero. -->
                    <tr
                      class="tx-group-row"
                      :class="{ 'tx-group-row--single': !esConsolidado(group) }"
                    >
                      <td colspan="11">
                        <v-icon small class="tx-group-row__icon">{{
                          esConsolidado(group) ? 'layers' : 'description'
                        }}</v-icon>
                        {{ etiquetaTramite(group) }}
                        <strong>{{ consecutivoDe(group) }}</strong>
                        &middot; {{ facturasDe(group) }}
                        <span
                          v-if="reingresoDe(group)"
                          class="tx-group-row__warn"
                          :title="reingresoAyuda(group)"
                        >
                          &middot; {{ reingresoDe(group) }}
                        </span>
                        <span
                          v-if="esConsolidado(group)"
                          class="tx-group-row__muted"
                        >
                          &middot; CIF total {{ money(cifTramiteDe(group)) }}
                        </span>
                        <span
                          v-if="controlFdDe(group)"
                          class="tx-group-row__muted"
                        >
                          &middot; Control F&amp;D: {{ controlFdDe(group) }}
                        </span>
                        <template v-if="esConsolidado(group)">
                          <span
                            v-if="group.pendingCount < group.invoices.length"
                          >
                            &middot; {{ group.pendingCount }} por iniciar
                          </span>
                          <span
                            v-if="group.multipleOrganos"
                            class="tx-group-row__warn"
                            :title="group.organosAnuentes.join(', ')"
                          >
                            &middot; varios órganos anuentes
                          </span>
                          <span
                            v-if="group.multipleDistribuidores"
                            class="tx-group-row__warn"
                            :title="group.distribuidores.join(', ')"
                          >
                            &middot; varios distribuidores
                          </span>
                        </template>
                        <span
                          v-else-if="group.organosAnuentes.length"
                          class="tx-group-row__muted"
                        >
                          &middot; {{ group.organosAnuentes.join(', ') }}
                        </span>
                      </td>
                    </tr>

                    <tr
                      v-for="(item, index) in group.invoices"
                      :key="item.id"
                      :class="{ 'tx-child-row': esConsolidado(group) }"
                    >
                      <td class="tx-cell--strong tx-cell--factura">
                        {{ item.invoiceNumber || '—' }}
                      </td>
                      <td>{{ distName(item) }}</td>
                      <td>{{ labName(item) }}</td>
                      <td class="tx-nowrap">{{ receivedDate(item) }}</td>
                      <td class="tx-nowrap">
                        <span v-if="item.admissionDate">
                          {{ admissionDate(item) }}
                        </span>
                        <span
                          v-else
                          class="tx-muted"
                          title="Se fija automáticamente al iniciar el trámite"
                        >
                          Al iniciar
                        </span>
                      </td>
                      <td>
                        <div
                          v-if="
                            item.organosAnuentes && item.organosAnuentes.length
                          "
                          class="organo-list"
                        >
                          <span
                            v-for="o in item.organosAnuentes"
                            :key="o.id"
                            class="organo-tag"
                          >
                            {{ o.name }}
                          </span>
                        </div>
                        <span v-else class="tx-muted">—</span>
                      </td>
                      <td class="th-center">
                        <button
                          class="notary-toggle"
                          :class="
                            item.notary
                              ? 'notary-toggle--yes'
                              : 'notary-toggle--no'
                          "
                          :disabled="notarySaving === item.id"
                          :title="
                            item.notary
                              ? 'Requiere notaría — clic para desmarcar'
                              : 'No requiere notaría — clic para marcar'
                          "
                          @click="toggleNotary(item)"
                        >
                          {{ item.notary ? '✅' : '❌' }}
                        </button>
                      </td>
                      <td class="td-comment">
                        <processing-comments :row="item" />
                        <input
                          type="text"
                          class="comment-input"
                          placeholder="Agregar comentario…"
                          value=""
                          maxlength="5000"
                          :disabled="commentSaving === item.id"
                          aria-label="Agregar comentario de Vanguard"
                          @change="saveComment(item, $event.target.value)"
                        />
                      </td>
                      <td>
                        <label
                          v-for="requirement in requirements"
                          :key="requirement.key"
                          class="processing-requirement"
                        >
                          <input
                            type="checkbox"
                            :checked="!!item[requirement.key]"
                            :disabled="
                              requirementSaving === item.id ||
                                item.checkCase === 'YES' ||
                                (requirement.key === 'documentsComplete' &&
                                  item.pendingDocument)
                            "
                            @change="
                              saveRequirement(
                                item,
                                requirement.key,
                                $event.target.checked
                              )
                            "
                          />
                          {{ requirement.text }}
                        </label>
                        <small v-if="item.pendingDocument"
                          >Resuelva Pendiente Documento en la factura.</small
                        >
                        <span
                          class="status-pill"
                          :class="
                            item.checkCase === 'YES'
                              ? 'status-pill--success'
                              : 'status-pill--danger'
                          "
                        >
                          {{
                            item.checkCase === 'YES' ? 'Iniciado' : 'Pendiente'
                          }}
                        </span>
                      </td>
                      <td class="th-center">
                        <v-btn
                          class="btn-detail"
                          icon
                          small
                          title="Ver detalles"
                          @click="handleDetails(item)"
                        >
                          <v-icon small>visibility</v-icon>
                        </v-btn>
                      </td>
                      <!-- Una sola vez por trámite: en un consolidado la celda
                           abarca todas sus facturas, porque se inicia
                           completo; iniciar una factura suelta lo partiría en
                           dos trámites. -->
                      <td
                        v-if="celdaTramite(group, index)"
                        :rowspan="filasTramite(group)"
                        class="th-center"
                        :class="{ 'tx-cell--tramite': esConsolidado(group) }"
                      >
                        <button
                          v-if="pendingOf(group).length"
                          class="tx-btn tx-btn--warning tx-btn--sm"
                          :title="iniciarTitle(group)"
                          :disabled="
                            loading ||
                              pendingOf(group).some(
                                row => missingRequirements(row).length
                              )
                          "
                          @click="startFrom(group, item)"
                        >
                          <v-icon small class="tx-btn__icon">play_arrow</v-icon>
                          Iniciar
                        </button>
                        <span v-else class="status-pill status-pill--success">
                          Iniciado
                        </span>
                      </td>
                    </tr>
                  </tbody>

                  <tbody v-if="pagination.loading">
                    <tr>
                      <td colspan="11" class="empty-state">Cargando...</td>
                    </tr>
                  </tbody>
                  <tbody v-else-if="groups.length === 0">
                    <tr>
                      <td colspan="11" class="empty-state">
                        No se encontraron facturas con los filtros aplicados.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>

        <v-flex xs12 v-if="pagination.pages">
          <div class="list-footer">
            <div class="list-footer__count">
              Mostrando {{ groups.length }} de {{ meta.totalGroups }} trámites
              ({{ meta.totalInvoices }}
              facturas)
            </div>
            <v-pagination
              v-model="pagination.page"
              :length="pagination.pages"
              :total-visible="7"
              color="#254f87"
              @input="asyncPagination"
            ></v-pagination>
          </div>
        </v-flex>
      </v-layout>
    </v-container>

    <v-dialog max-width="1024" v-if="detail.data" v-model="detail.show">
      <compose
        :item="detail.data"
        :key="detail.key"
        v-on:close-detail="detail.show = false"
      ></compose>
    </v-dialog>

    <v-dialog max-width="500" v-if="dialog.message" v-model="dialog.show">
      <message
        :key="dialog.key"
        :options="dialog"
        v-on:close="dialog.show = false"
      ></message>
    </v-dialog>

    <v-dialog max-width="350" persistent v-model="loading">
      <loading></loading>
    </v-dialog>
  </div>
</template>

<script>
import ProcessingComments from '@/components/ProcessingComments'
import compose from './../dialog/DialogDetails'
import loading from './../dialog/DialogLoading'
import message from './../dialog/Message'
import TxFilter from './TransitFilter.vue'
import SessionHelper from '@/helpers/SessionHelper'
import moment from 'moment-timezone'
import TramiteBand from './mixins/TramiteBand'

export default {
  // Banda del trámite compartida con Contabilidad (consecutivo, facturas, CIF).
  mixins: [TramiteBand],
  components: {
    ProcessingComments,
    compose,
    loading,
    message,
    'tx-filter': TxFilter,
  },
  data() {
    return {
      loading: true,
      // El backend devuelve los tramites ya agrupados (mismo formato que
      // Listado de Facturas): un grupo = un consolidado o una factura suelta.
      groups: [],
      meta: { totalGroups: 0, totalInvoices: 0 },
      pagination: {
        page: 1,
        rpp: this.$config.resultsPerPage,
        loading: true,
        pages: 0,
        search: {},
      },
      detail: { show: null, data: null, key: 0 },
      dialog: { show: null, key: 0 },
      // Id del trámite cuyo toggle de Notaría se está guardando (evita doble clic).
      notarySaving: null,
      commentSaving: null,
      requirementSaving: null,
      requirements: [
        { key: 'predeclarationReady', text: 'Predeclaración terminada' },
        { key: 'documentsComplete', text: 'Documentos completos' },
      ],
    }
  },
  created() {
    this.asyncPagination()
  },
  methods: {
    missingRequirements(row) {
      return [
        !row.predeclarationReady && 'Predeclaración terminada',
        (!row.documentsComplete || row.pendingDocument) &&
          'Documentos completos',
      ].filter(Boolean)
    },
    async saveRequirement(item, key, value) {
      this.requirementSaving = item.id
      try {
        const response = await this.$store.dispatch(
          'invoices/updateProcessFields',
          { id: item.id, [key]: value }
        )
        if (!response || response.status >= 400)
          throw new Error(response && response.data && response.data.error)
        item[key] = value
      } catch (error) {
        this.notify(
          error.message || 'No se pudo guardar el requisito.',
          'error'
        )
      } finally {
        this.requirementSaving = null
      }
    },
    labName(item) {
      return item.laboratorio || '—'
    },
    distName(item) {
      return item.distribuidor || '—'
    },
    // Facturas del grupo que aun no han iniciado; el backend ya marca cada
    // fila con `pending` y trae el conteo en `pendingCount`.
    pendingOf(group) {
      return group.invoices.filter(i => i.pending)
    },
    // Un trámite rechazado que volvió a entrar y aún falta iniciar. El
    // backend lo pone al principio de la bandeja; la banda dice por qué.
    reingresosDe(group) {
      return this.pendingOf(group).filter(i => i.deniedDate && !i.fdApproved)
    },
    reingresoDe(group) {
      const reingresos = this.reingresosDe(group)
      if (!reingresos.length) return ''
      const ultima = reingresos
        .map(i => i.reinitDate)
        .filter(Boolean)
        .sort()
        .pop()
      return ultima
        ? `Reingreso del ${moment(ultima).format('DD/MM/YYYY')}`
        : 'Reingreso'
    },
    // El motivo del rechazo se conserva al reingresar: la banda lo muestra al
    // pasar el cursor.
    reingresoAyuda(group) {
      const motivos = this.reingresosDe(group)
        .map(i => (i.observationsDenied || '').trim())
        .filter(Boolean)
      const motivo = Array.from(new Set(motivos)).join(' / ')
      return motivo
        ? `Fue rechazado y volvió a entrar. Motivo del rechazo: ${motivo}`
        : 'Fue rechazado y volvió a entrar.'
    },
    receivedDate(item) {
      return item.receivedDate
        ? moment(item.receivedDate).format('DD/MM/YYYY')
        : '—'
    },
    admissionDate(item) {
      return item.admissionDate
        ? moment(item.admissionDate).format('DD/MM/YYYY')
        : '—'
    },
    helperPaginate(response) {
      this.groups = (response && response.groups) || []
      this.meta = {
        totalGroups: (response && response.totalGroups) || 0,
        totalInvoices: (response && response.totalInvoices) || 0,
      }
      // La paginacion es por GRUPO, no por factura: asi un consolidado nunca
      // queda partido entre dos paginas.
      this.pagination.pages =
        Math.ceil(this.meta.totalGroups / this.pagination.rpp) || 0
      this.loading = false
      this.pagination.loading = false
    },
    asyncPagination() {
      this.pagination.loading = true
      const filter = {
        // Bandeja de pendientes: el backend deja solo los grupos que aun
        // tienen alguna factura sin iniciar (checkCase != YES). Sin este
        // option el listado traia las 7.500+ facturas historicas.
        option: 'to-process',
        rpp: this.pagination.rpp,
        page: this.pagination.page,
        ...this.pagination.search,
      }
      return new Promise(resolve => {
        this.$store
          .dispatch('invoices/getConsolidated', filter)
          .then(response => {
            this.helperPaginate(response)
            return resolve()
          })
      })
    },
    // Inicia el trámite de todas las facturas pendientes de un consolidado en
    // una sola acción.
    // Inicia el tramite de TODAS las facturas pendientes del consolidado en una
    // sola accion: caseReady marca checkCase = 'YES', que es justo lo que la
    // pantalla "Tramites en Proceso" filtra (checkCase = YES y fdApproved null).
    // El botón va una vez por trámite; cambia el alcance: un consolidado se
    // inicia entero, porque iniciar una factura suelta lo partiría en dos
    // trámites distintos.
    startFrom(group, item) {
      if (this.esConsolidado(group)) {
        this.startGroup(group)
        return
      }
      this.startProcess(item)
    },
    iniciarTitle(group) {
      const missing = [
        ...new Set(
          this.pendingOf(group).reduce(
            (all, row) => all.concat(this.missingRequirements(row)),
            []
          )
        ),
      ]
      if (missing.length) return `Falta: ${missing.join(' y ')}`
      return this.esConsolidado(group)
        ? `Pasa las ${
            group.pendingCount
          } facturas del consolidado a Trámites en Proceso`
        : 'Iniciar el trámite de esta factura'
    },
    startGroup(group) {
      const ids = this.pendingOf(group).map(i => i.id)
      if (!ids.length) return
      this.loading = true
      this.$store
        .dispatch('transit/caseReady', { invoices: ids, sendEmails: true })
        .then(() => this.asyncPagination())
        .then(() => {
          // Mismo consecutivo que la banda (CONS-), no el control F&D viejo.
          const numero = this.consecutivoDe(group)
          this.dialog = {
            message:
              `El consolidado ${numero} pasó a Trámites en ` +
              `Proceso con sus ${ids.length} facturas.`,
            color: 'success',
            show: true,
            key: this.dialog.key + 1,
          }
        })
        .catch(error => {
          this.loading = false
          this.dialog = {
            message:
              (error.response &&
                error.response.data &&
                error.response.data.error) ||
              'No se pudo iniciar el consolidado.',
            color: 'error',
            show: true,
            key: this.dialog.key + 1,
          }
        })
    },
    // "Iniciar trámite" por fila: es la misma acción que "Valija lista" pero
    // para un solo trámite (marca checkCase=YES y fija la fecha de admisión).
    startProcess(item) {
      if (item.checkCase === 'YES') return
      this.loading = true
      this.$store
        .dispatch('transit/caseReady', {
          invoices: [item.id],
          sendEmails: true,
        })
        .then(() => this.asyncPagination())
        .then(() => {
          this.dialog = {
            message: `El trámite ${
              item.invoiceNumber
            } pasó a Trámites en Proceso.`,
            color: 'success',
            show: true,
            key: this.dialog.key + 1,
          }
        })
        .catch(error => {
          this.loading = false
          this.dialog = {
            message:
              (error.response &&
                error.response.data &&
                error.response.data.error) ||
              'No se pudo iniciar el trámite.',
            color: 'error',
            show: true,
            key: this.dialog.key + 1,
          }
        })
    },
    // Notaría ❌/✅: alterna y persiste. Actualiza la fila de forma optimista y
    // revierte si el guardado falla.
    toggleNotary(item) {
      if (this.notarySaving) return
      const next = !item.notary
      this.notarySaving = item.id
      item.notary = next
      this.$store
        .dispatch('invoices/updateProcessFields', {
          id: item.id,
          notary: next,
        })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            item.notary = !next
            this.notify('No se pudo actualizar la Notaría.', 'error')
          }
        })
        .catch(() => {
          item.notary = !next
          this.notify('No se pudo actualizar la Notaría.', 'error')
        })
        .finally(() => {
          this.notarySaving = null
        })
    },
    // Comentario libre: se guarda al salir del campo (evento change), no en cada
    // tecla, para no disparar una petición por carácter.
    async saveComment(item, value) {
      const clean = (value || '').trim()
      if (!clean || this.commentSaving) return
      this.commentSaving = item.id
      try {
        const response = await this.$store.dispatch(
          'invoices/updateProcessFields',
          { id: item.id, processComment: clean }
        )
        if (!response || response.status >= 400)
          throw new Error('No se pudo guardar el comentario.')
        this.$set(item, 'processComments', response.data.processComments)
        item.processComment = response.data.processComment
        await this.asyncPagination()
      } catch (error) {
        this.notify(
          error.message || 'No se pudo guardar el comentario.',
          'error'
        )
      } finally {
        this.commentSaving = null
      }
    },
    notify(msg, color) {
      this.dialog = {
        message: msg,
        color: color || 'info',
        show: true,
        key: this.dialog.key + 1,
      }
    },
    handleDetails(row) {
      this.loading = true
      this.$store
        .dispatch('invoices/getItems', { invoiceId: row.id })
        .then(items => {
          this.loading = false
          this.detail = {
            // El listado agrupado trae laboratorio/distribuidor como texto,
            // pero DialogDetails espera los objetos lab_invoices/dist_invoices.
            data: {
              ...row,
              lab_invoices: { id: row.labId, name: row.laboratorio },
              dist_invoices: { id: row.distId, name: row.distribuidor },
              products: items ? items : null,
            },
            show: true,
            key: this.detail.key + 1,
          }
        })
    },
    onSearch(params) {
      Object.keys(params).forEach(key => {
        if (params[key] == null || params[key] === '') delete params[key]
      })
      this.pagination.search = params
      this.pagination.page = 1
      this.asyncPagination()
    },
    onDownload() {
      const filter = {
        ...this.pagination.search,
        token: SessionHelper.token,
      }
      this.$socket.emit('exportTransitInvoices', filter)
    },
  },
}
</script>

<style scoped src="./transit-list.css"></style>

<style scoped>
/* Columnas nuevas de "Trámites para procesar". El resto de la tabla usa
   transit-list.css; aquí sólo lo que esas columnas agregan. */
.tx-nowrap {
  white-space: nowrap;
}

/* Órgano Anuente: puede haber varios por trámite, se listan como etiquetas. */
.organo-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.organo-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  background: #eef2f7;
  color: #254f87;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

/* Botón "Iniciar" más compacto que el de la barra de acciones. */
.tx-btn--sm {
  height: 28px;
  padding: 0 10px;
  font-size: 12px;
}

/* Toggle de Notaría: el emoji es el control; el botón sólo da área de clic. */
.notary-toggle {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 4px 6px;
  border-radius: 6px;
  transition: background 0.15s ease;
}
.notary-toggle:hover:not(:disabled) {
  background: #f1f5f9;
}
.notary-toggle:disabled {
  opacity: 0.5;
  cursor: default;
}

/* Comentario editable inline. Discreto hasta que recibe foco. */
.td-comment {
  min-width: 180px;
}
.comment-input {
  width: 100%;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 13px;
  color: #0f172a;
  background: #f8fafc;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.comment-input:hover {
  border-color: #e2e8f0;
}
.comment-input:focus {
  outline: none;
  border-color: #254f87;
  background: #ffffff;
}
.comment-input::placeholder {
  color: #94a3b8;
}
</style>

<style scoped>
.processing-requirement {
  display: flex;
  gap: 6px;
  align-items: center;
  margin-bottom: 8px;
}
.tx-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>
